import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Banner, Spacer, spaces, TextInputField } from '@electricjs/arc';

import {
  AddressGroupField,
  getRequiredAddressValidationSchema,
} from '@/components/AddressGroupField';

import { BillingInfo } from '@/types/billing';
import { emailRegex } from '@/constants/email';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useSaveBillingInfoMutation } from '@/redux/slices/billingApiSlice';
import { useGlobalUI } from '@/components/GlobalUIProvider';

const BillingInfoBanner = () => {
  const { t } = useTranslation('billing');
  return (
    <Banner
      id="billing-info-banner"
      isFlat
      title={t('billingInfoForm.banner.title')}
      message={t('billingInfoForm.banner.message')}
    />
  );
};

const getBillingInfoSchema = (t: TFunction) =>
  yup.object().shape({
    email: yup
      .string()
      .matches(emailRegex, t('schemas.billingInfoSchema.emailInvalid'))
      .required(t('schemas.billingInfoSchema.emailRequired')),
    address: getRequiredAddressValidationSchema(),
  });

const defaultValues: BillingInfo = {
  email: '',
  address: {
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  },
};

type BillingInfoFormProps = {
  onClose: () => void;
  billingInfo?: BillingInfo;
  setHandleSubmit: (handleSubmit: null | (() => void)) => void;
  setIsSubmitReady: (isSubmitReady: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
};

const BillingInfoForm = ({
  onClose,
  billingInfo,
  setHandleSubmit,
  setIsSubmitReady,
  setIsLoading,
}: BillingInfoFormProps) => {
  const { t } = useTranslation('billing');
  const { showSuccessToast, showErrorToast } = useGlobalUI();

  const organizationId = useGetOrganizationId();
  const [saveBillingInfo, { isLoading }] = useSaveBillingInfoMutation();
  const { space_l } = spaces;

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
  } = useForm<BillingInfo>({
    resolver: yupResolver(getBillingInfoSchema(t)),
    mode: 'onTouched',
    defaultValues,
  });

  useEffect(() => {
    if (billingInfo) {
      setValue('email', billingInfo.email);
      setValue('address', billingInfo.address);
    }
  }, [billingInfo, setValue]);

  const submitForm = useCallback(() => {
    handleSubmit((formData: BillingInfo) => {
      // call backend to save the billing info
      saveBillingInfo({
        organizationId,
        billingInfo: formData,
      })
        .unwrap()
        .then(() => {
          {
            billingInfo
              ? showSuccessToast({
                  id: 'edit-billing-info-success-toast',
                  title: t('billingInfoForm.toasts.editSuccess'),
                  message: t('billingInfoForm.toasts.editSuccessMessage'),
                  stack: true,
                })
              : showSuccessToast({
                  id: 'save-billing-info-success-toast',
                  title: t('billingInfoForm.toasts.saveSuccess'),
                  message: t('billingInfoForm.toasts.saveSuccessMessage'),
                  stack: true,
                });
          }
          onClose();
        })
        .catch(() => {
          showErrorToast({
            id: 'save-billing-info-error-toast',
            message: t('billingInfoForm.toasts.error'),
          });
        });
    })();
  }, [
    billingInfo,
    organizationId,
    saveBillingInfo,
    showErrorToast,
    showSuccessToast,
    onClose,
    handleSubmit,
    t,
  ]);

  const isSubmitReady = isValid && !isLoading;

  useEffect(() => {
    setHandleSubmit(submitForm);

    return () => {
      setHandleSubmit(null);
    };
  }, [submitForm, setHandleSubmit]);

  useEffect(() => {
    setIsSubmitReady(isSubmitReady);

    return () => {
      setIsSubmitReady(false);
    };
  }, [isSubmitReady, setIsSubmitReady, isValid, isLoading]);

  useEffect(() => {
    setIsLoading(isLoading);

    return () => {
      setIsLoading(false);
    };
  }, [isLoading, setIsLoading]);

  return (
    <>
      {billingInfo && (
        <>
          <BillingInfoBanner />
          <Spacer y={space_l} />
        </>
      )}
      <TextInputField
        id="billing-info-email"
        name="email"
        control={control}
        label={t('billingInfoForm.labels.email')}
        placeholder={t('billingInfoForm.placeholders.email')}
        required
      />
      <AddressGroupField
        name="address"
        control={control}
        onChange={address =>
          setValue('address', address, { shouldValidate: true })
        }
        googleMapsApiKey={window._env_?.VITE_GOOGLE_MAPS_PUB_API_KEY}
        required
      />
    </>
  );
};

export default BillingInfoForm;
